import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNoizaMetrics, DateRange } from '../../lib/noiza-metrics-hooks';
import { formatCurrency } from '../../lib/utils';
import { TrendingUpIcon, ShoppingCartIcon, ReceiptIcon, StoreIcon, UsersIcon, UserCheckIcon, ArrowLeftIcon, ActivityIcon, RefreshCwIcon, Loader2Icon, CalendarIcon, FilterIcon, ClockIcon, AwardIcon, AlertTriangleIcon, ChevronDownIcon } from 'lucide-react';
// ─── Helpers ───────────────────────────────────────────────
function formatCompactCurrency(value: number): string {
  if (value >= 1000000) return `₡${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `₡${(value / 1000).toFixed(0)}k`;
  return formatCurrency(value);
}
function formatDayLabel(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${d} ${months[m - 1]}`;
  } catch {
    return dateStr;
  }
}
function normalizeChannel(ch: string): string {
  const lower = (ch || '').toLowerCase();
  if (lower.includes('qr') || lower.includes('self')) return 'QR';
  if (lower.includes('waiter') || lower.includes('mesero')) return 'Mesero';
  if (lower.includes('web') || lower.includes('online')) return 'Web';
  if (lower.includes('caja') || lower.includes('cashier')) return 'Caja';
  return ch || 'Otro';
}
const CHANNEL_COLORS: Record<string, string> = {
  QR: '#10b981',
  Mesero: '#6366f1',
  Web: '#f59e0b',
  Caja: '#8b5cf6',
  Otro: '#6b7280'
};
function getDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}
function getToday(): string {
  return new Date().toISOString().split('T')[0];
}
function getFirstDayOfMonth(): string {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
}
// ─── Dark Tooltip ──────────────────────────────────────────
function DarkTooltip({
  active,
  payload,
  label,
  isCurrency
}: any) {
  if (!active || !payload?.length) return null;
  return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-4 py-3 rounded-xl shadow-2xl text-sm z-50" data-id="element-3282">
      <p className="font-medium text-white/60 mb-1.5 text-xs uppercase tracking-wider" data-id="element-3283">
        {label}
      </p>
      {payload.map((entry: any, i: number) => <p key={i} className="flex items-center gap-2" data-id="element-3284">
          <span className="w-2 h-2 rounded-full" style={{
        backgroundColor: entry.color || entry.fill
      }} data-id="element-3285" />
          <span className="text-white/50" data-id="element-3286">{entry.name}:</span>
          <span className="font-semibold text-white" data-id="element-3287">
            {isCurrency ? formatCurrency(Number(entry.value ?? 0)) : Number(entry.value ?? 0).toLocaleString()}
          </span>
        </p>)}
    </div>;
}
// ─── KPI Card ──────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  accentColor: string;
}
function KpiCard({
  label,
  value,
  subtitle,
  icon,
  accentColor
}: KpiCardProps) {
  return <div className="relative overflow-hidden rounded-2xl bg-[#12121a] border border-white/[0.06] p-5 group" data-id="element-3288">
      <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-[0.07] -translate-y-8 translate-x-8`} style={{
      backgroundColor: accentColor
    }} data-id="element-3289" />
      <div className="relative" data-id="element-3290">
        <div className="flex items-center justify-between mb-4" data-id="element-3291">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
          backgroundColor: `${accentColor}15`
        }} data-id="element-3292">
            {icon}
          </div>
        </div>
        <p className="text-[13px] font-medium text-white/40 uppercase tracking-wider" data-id="element-3293">
          {label}
        </p>
        <p className="text-3xl font-bold text-white mt-1 tracking-tight" data-id="element-3294">
          {value}
        </p>
        {subtitle && <p className="text-xs text-white/30 mt-1" data-id="element-3295">{subtitle}</p>}
      </div>
    </div>;
}
// ─── Main Page ─────────────────────────────────────────────
export function NoizaMetricsPage() {
  const [restaurantId, setRestaurantId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: getDaysAgo(30),
    to: getToday()
  });
  const [datePreset, setDatePreset] = useState<string>('30d');
  const {
    data,
    loading,
    error,
    refetch,
    restaurants
  } = useNoizaMetrics(restaurantId, dateRange);
  // Set default restaurant to Capricho de Canela if available and not yet set
  useEffect(() => {
    if (restaurants.length > 0 && restaurantId === 'all') {
      const capricho = restaurants.find(r => r.slug === 'capricho-de-canela');
      if (capricho) {
        setRestaurantId(capricho.id);
      }
    }
  }, [restaurants]);
  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const to = getToday();
    let from = to;
    if (preset === 'today') from = to;else if (preset === '7d') from = getDaysAgo(7);else if (preset === '30d') from = getDaysAgo(30);else if (preset === 'month') from = getFirstDayOfMonth();
    setDateRange({
      from,
      to
    });
  };
  // Derived Data for Charts
  const dailyChartData = useMemo(() => {
    const source = data.salesDaily.length > 0 ? data.salesDaily : data.gmvDaily;
    // Aggregate by day if multiple restaurants
    const aggregated = source.reduce((acc: any, curr: any) => {
      const day = curr.day;
      if (!acc[day]) acc[day] = {
        day,
        label: formatDayLabel(day),
        gmv: 0,
        orders: 0
      };
      acc[day].gmv += curr.gmv;
      acc[day].orders += curr.orders;
      return acc;
    }, {});
    return Object.values(aggregated).sort((a: any, b: any) => a.day.localeCompare(b.day));
  }, [data.salesDaily, data.gmvDaily]);
  const hourlyChartData = useMemo(() => {
    const aggregated = data.salesHourly.reduce((acc: any, curr: any) => {
      const hour = curr.hour;
      if (!acc[hour]) acc[hour] = {
        hour,
        label: `${hour}:00`,
        gmv: 0,
        orders: 0
      };
      acc[hour].gmv += curr.gmv;
      acc[hour].orders += curr.orders;
      return acc;
    }, {});
    return Object.values(aggregated).sort((a: any, b: any) => Number(a.hour) - Number(b.hour));
  }, [data.salesHourly]);
  const channelChartData = useMemo(() => {
    return data.channelDistribution.map(d => ({
      name: normalizeChannel(d.channel),
      value: d.orders,
      pct: d.pct,
      color: CHANNEL_COLORS[normalizeChannel(d.channel)] || '#6b7280'
    })).filter(d => d.value > 0);
  }, [data.channelDistribution]);
  const totalChannelOrders = channelChartData.reduce((sum, d) => sum + d.value, 0);
  const topRestaurantsChartData = useMemo(() => {
    return data.gmvByRestaurant.slice(0, 5).map(r => ({
      name: String(r.restaurant_name || '').length > 15 ? String(r.restaurant_name || '').substring(0, 15) + '...' : String(r.restaurant_name || ''),
      fullName: r.restaurant_name,
      gmv: r.gmv
    }));
  }, [data.gmvByRestaurant]);
  const topProductsChartData = useMemo(() => {
    return data.topProducts.slice(0, 5).map(p => ({
      name: String(p.product_name || '').length > 15 ? String(p.product_name || '').substring(0, 15) + '...' : String(p.product_name || ''),
      fullName: p.product_name,
      revenue: p.revenue,
      quantity: p.quantity
    }));
  }, [data.topProducts]);
  // Insights
  const insights = useMemo(() => {
    let bestDay = {
      day: '-',
      val: 0
    };
    let worstDay = {
      day: '-',
      val: Infinity
    };
    dailyChartData.forEach((d: any) => {
      if (d.gmv > bestDay.val) bestDay = {
        day: d.label,
        val: d.gmv
      };
      if (d.gmv < worstDay.val && d.gmv > 0) worstDay = {
        day: d.label,
        val: d.gmv
      };
    });
    let bestHour = {
      hour: '-',
      val: 0
    };
    let worstHour = {
      hour: '-',
      val: Infinity
    };
    hourlyChartData.forEach((d: any) => {
      if (d.gmv > bestHour.val) bestHour = {
        hour: d.label,
        val: d.gmv
      };
      if (d.gmv < worstHour.val && d.gmv > 0) worstHour = {
        hour: d.label,
        val: d.gmv
      };
    });
    let bestRest = data.gmvByRestaurant[0];
    let worstRest = data.gmvByRestaurant[data.gmvByRestaurant.length - 1];
    return {
      bestDay,
      worstDay,
      bestHour,
      worstHour,
      bestRest,
      worstRest
    };
  }, [dailyChartData, hourlyChartData, data.gmvByRestaurant]);
  return <div className="min-h-screen bg-[#0a0a0f] text-white pb-20" data-id="element-3296">
      {/* ─── Header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/[0.06]" data-id="element-3297">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3298">
          <div className="flex items-center gap-4" data-id="element-3299">
            <Link to="/admin/enrollment" className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center hover:bg-white/[0.1] transition-colors" data-id="element-3300">
              <ArrowLeftIcon className="w-4 h-4 text-white/60" data-id="element-3301" />
            </Link>
            <div data-id="element-3302">
              <div className="flex items-center gap-2.5" data-id="element-3303">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20" data-id="element-3304">
                  <ActivityIcon className="w-4 h-4 text-white" data-id="element-3305" />
                </div>
                <h1 className="text-lg font-bold tracking-tight" data-id="element-3306">NOIZA</h1>
                <span className="text-xs font-medium text-white/30 bg-white/[0.06] px-2 py-0.5 rounded-md" data-id="element-3307">
                  Executive Dashboard
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-3308">
            <button onClick={refetch} disabled={loading} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] transition-colors text-sm text-white/60 hover:text-white/80 disabled:opacity-50" data-id="element-3309">
              {loading ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-3310" /> : <RefreshCwIcon className="w-4 h-4" data-id="element-3311" />}
              <span className="hidden sm:inline" data-id="element-3312">Actualizar</span>
            </button>
          </div>
        </div>
      </header>

      {/* ─── Filters Bar ────────────────────────────────── */}
      <div className="bg-[#12121a] border-b border-white/[0.06]" data-id="element-3313">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-id="element-3314">
          <div className="flex items-center gap-3" data-id="element-3315">
            <FilterIcon className="w-4 h-4 text-white/40" data-id="element-3316" />
            <div className="relative" data-id="element-3317">
              <select value={restaurantId} onChange={e => setRestaurantId(e.target.value)} className="appearance-none bg-white/[0.06] border border-white/[0.1] rounded-lg pl-3 pr-8 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors" data-id="element-3318">
                <option value="all" data-id="element-3319">Todos los restaurantes</option>
                {restaurants.map(r => <option key={r.id} value={r.id} data-id="element-3320">
                    {r.name}
                  </option>)}
              </select>
              <ChevronDownIcon className="w-4 h-4 text-white/40 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-3321" />
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 hide-scrollbar" data-id="element-3322">
            <CalendarIcon className="w-4 h-4 text-white/40 mr-1" data-id="element-3323" />
            {[{
            id: 'today',
            label: 'Hoy'
          }, {
            id: '7d',
            label: '7 días'
          }, {
            id: '30d',
            label: '30 días'
          }, {
            id: 'month',
            label: 'Mes actual'
          }].map(preset => <button key={preset.id} onClick={() => handleDatePreset(preset.id)} className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${datePreset === preset.id ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/[0.03] text-white/60 border border-white/[0.05] hover:bg-white/[0.08]'}`} data-id="element-3324">
                {preset.label}
              </button>)}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-id="element-3325">
        {error && <div className="rounded-2xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400 flex items-center gap-3" data-id="element-3326">
            <AlertTriangleIcon className="w-5 h-5" data-id="element-3327" />
            {error}
          </div>}

        {loading ? <div className="space-y-8" data-id="element-3328">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-3329">
              {Array.from({
            length: 6
          }).map((_, i) => <div key={i} className="h-32 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-3330" />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-3331">
              <div className="lg:col-span-2 h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-3332" />
              <div className="h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-3333" />
            </div>
          </div> : <>
            {/* ─── KPI Cards ──────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-3334">
              <KpiCard label="GMV Total" value={formatCompactCurrency(Number(data.gmv.total_gmv ?? 0))} subtitle="Ingresos brutos" icon={<TrendingUpIcon className="w-5 h-5 text-emerald-400" data-id="element-3336" />} accentColor="#10b981" data-id="element-3335" />
              <KpiCard label="Órdenes" value={Number(data.gmv.total_orders ?? 0).toLocaleString()} subtitle="Total procesadas" icon={<ShoppingCartIcon className="w-5 h-5 text-blue-400" data-id="element-3338" />} accentColor="#3b82f6" data-id="element-3337" />
              <KpiCard label="Ticket Promedio" value={formatCurrency(Number(data.gmv.avg_ticket ?? 0))} subtitle="Por orden" icon={<ReceiptIcon className="w-5 h-5 text-amber-400" data-id="element-3340" />} accentColor="#f59e0b" data-id="element-3339" />
              <KpiCard label="Clientes Únicos" value={Number(data.uniqueCustomers ?? 0).toLocaleString()} subtitle="Compradores distintos" icon={<UsersIcon className="w-5 h-5 text-cyan-400" data-id="element-3342" />} accentColor="#06b6d4" data-id="element-3341" />
              <KpiCard label="Recurrentes" value={Number(data.repeatCustomers.repeat_count ?? 0).toLocaleString()} subtitle={Number(data.repeatCustomers.repeat_pct ?? 0) > 0 ? `${Math.round(Number(data.repeatCustomers.repeat_pct ?? 0))}% del total` : 'Clientes que regresan'} icon={<UserCheckIcon className="w-5 h-5 text-pink-400" data-id="element-3344" />} accentColor="#ec4899" data-id="element-3343" />
              <KpiCard label="Autoservicio" value={`${Math.round(Number(data.selfServicePct.self_service_pct ?? 0))}%`} subtitle={`Mesero: ${Math.round(Number(data.selfServicePct.waiter_pct ?? 0))}%`} icon={<StoreIcon className="w-5 h-5 text-purple-400" data-id="element-3346" />} accentColor="#8b5cf6" data-id="element-3345" />
            </div>

            {/* ─── Insights Section ───────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3347">
              <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 p-4 flex items-start gap-4" data-id="element-3348">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0" data-id="element-3349">
                  <AwardIcon className="w-5 h-5 text-emerald-400" data-id="element-3350" />
                </div>
                <div data-id="element-3351">
                  <h3 className="text-sm font-semibold text-emerald-400" data-id="element-3352">
                    Mejor Día
                  </h3>
                  <p className="text-white font-medium mt-1" data-id="element-3353">
                    {insights.bestDay.day}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-3354">
                    {formatCurrency(insights.bestDay.val)}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 p-4 flex items-start gap-4" data-id="element-3355">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0" data-id="element-3356">
                  <ClockIcon className="w-5 h-5 text-blue-400" data-id="element-3357" />
                </div>
                <div data-id="element-3358">
                  <h3 className="text-sm font-semibold text-blue-400" data-id="element-3359">
                    Hora Pico
                  </h3>
                  <p className="text-white font-medium mt-1" data-id="element-3360">
                    {insights.bestHour.hour}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-3361">
                    {formatCurrency(insights.bestHour.val)}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 p-4 flex items-start gap-4" data-id="element-3362">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0" data-id="element-3363">
                  <StoreIcon className="w-5 h-5 text-amber-400" data-id="element-3364" />
                </div>
                <div data-id="element-3365">
                  <h3 className="text-sm font-semibold text-amber-400" data-id="element-3366">
                    Top Restaurante
                  </h3>
                  <p className="text-white font-medium mt-1 truncate max-w-[150px]" data-id="element-3367">
                    {insights.bestRest?.restaurant_name || '-'}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-3368">
                    {insights.bestRest ? formatCurrency(insights.bestRest.gmv) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* ─── Charts Row 1 ───────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-3369">
              {/* GMV Daily Chart */}
              <div className="lg:col-span-2 rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3370">
                <div className="flex items-center justify-between mb-6" data-id="element-3371">
                  <div data-id="element-3372">
                    <h2 className="text-sm font-semibold text-white/80" data-id="element-3373">
                      GMV Diario
                    </h2>
                    <p className="text-xs text-white/30 mt-0.5" data-id="element-3374">
                      Ingresos brutos por día
                    </p>
                  </div>
                </div>
                {dailyChartData.length === 0 ? <div className="h-64 flex items-center justify-center text-white/20 text-sm" data-id="element-3375">
                    Sin datos
                  </div> : <div className="h-64" data-id="element-3376">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3377">
                      <AreaChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-3378">
                        <defs data-id="element-3379">
                          <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-3380">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} data-id="element-3381" />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0} data-id="element-3382" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-3383" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval="preserveStartEnd" data-id="element-3384" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-3385" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-3387" />} data-id="element-3386" />
                        <Area type="monotone" dataKey="gmv" name="GMV" stroke="#10b981" strokeWidth={2} fill="url(#gmvGrad)" data-id="element-3388" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Channel Distribution */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3389">
                <div className="mb-4" data-id="element-3390">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3391">
                    Canales
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-3392">
                    Distribución de pedidos
                  </p>
                </div>
                {channelChartData.length === 0 ? <div className="h-48 flex items-center justify-center text-white/20 text-sm" data-id="element-3393">
                    Sin datos
                  </div> : <>
                    <div className="h-48" data-id="element-3394">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-3395">
                        <PieChart data-id="element-3396">
                          <Pie data={channelChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" strokeWidth={0} data-id="element-3397">
                            {channelChartData.map((entry, i) => <Cell key={i} fill={entry.color} data-id="element-3398" />)}
                          </Pie>
                          <Tooltip content={({
                      active,
                      payload
                    }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      const pct = totalChannelOrders > 0 ? Math.round(d.value / totalChannelOrders * 100) : 0;
                      return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-3 py-2 rounded-xl shadow-2xl text-sm" data-id="element-3400">
                                  <p className="font-medium" data-id="element-3401">{d.name}</p>
                                  <p className="text-white/50" data-id="element-3402">
                                    {Number(d.value ?? 0).toLocaleString()}{' '}
                                    pedidos ({pct}%)
                                  </p>
                                </div>;
                    }} data-id="element-3399" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 mt-2" data-id="element-3403">
                      {channelChartData.map(entry => {
                  const pct = totalChannelOrders > 0 ? Math.round(entry.value / totalChannelOrders * 100) : 0;
                  return <div key={entry.name} className="flex items-center justify-between" data-id="element-3404">
                            <div className="flex items-center gap-2" data-id="element-3405">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                        backgroundColor: entry.color
                      }} data-id="element-3406" />
                              <span className="text-sm text-white/60" data-id="element-3407">
                                {entry.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-3" data-id="element-3408">
                              <span className="text-sm font-semibold text-white/90" data-id="element-3409">
                                {Number(entry.value ?? 0).toLocaleString()}
                              </span>
                              <span className="text-xs text-white/30 w-10 text-right" data-id="element-3410">
                                {pct}%
                              </span>
                            </div>
                          </div>;
                })}
                    </div>
                  </>}
              </div>
            </div>

            {/* ─── Charts Row 2 ───────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3411">
              {/* Hourly Sales */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3412">
                <div className="mb-6" data-id="element-3413">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3414">
                    Ventas por Hora
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-3415">
                    Distribución a lo largo del día
                  </p>
                </div>
                {hourlyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-3416">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-3417">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3418">
                      <BarChart data={hourlyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-3419">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-3420" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} data-id="element-3421" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-3422" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-3424" />} data-id="element-3423" />
                        <Bar dataKey="gmv" name="GMV" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-3425" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Orders Daily */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3426">
                <div className="mb-6" data-id="element-3427">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3428">
                    Órdenes Diarias
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-3429">
                    Volumen de pedidos por día
                  </p>
                </div>
                {dailyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-3430">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-3431">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3432">
                      <BarChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-3433">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-3434" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval="preserveStartEnd" data-id="element-3435" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} data-id="element-3436" />
                        <Tooltip content={<DarkTooltip isCurrency={false} data-id="element-3438" />} data-id="element-3437" />
                        <Bar dataKey="orders" name="Órdenes" fill="#8b5cf6" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-3439" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>

            {/* ─── Charts Row 3 (Horizontal Bars) ──────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3440">
              {/* Top Restaurants */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3441">
                <div className="mb-6" data-id="element-3442">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3443">
                    Top Restaurantes
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-3444">
                    Por GMV generado
                  </p>
                </div>
                {topRestaurantsChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-3445">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-3446">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3447">
                      <BarChart data={topRestaurantsChartData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-3448">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} data-id="element-3449" />
                        <XAxis type="number" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-3450" />
                        <YAxis dataKey="name" type="category" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.6)'
                  }} tickLine={false} axisLine={false} width={100} data-id="element-3451" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-3453" />} data-id="element-3452" />
                        <Bar dataKey="gmv" name="GMV" fill="#f59e0b" radius={[0, 3, 3, 0]} barSize={20} opacity={0.8} data-id="element-3454" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Top Products */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3455">
                <div className="mb-6" data-id="element-3456">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3457">
                    Top Productos
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-3458">
                    Por ingresos generados
                  </p>
                </div>
                {topProductsChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-3459">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-3460">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3461">
                      <BarChart data={topProductsChartData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-3462">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} data-id="element-3463" />
                        <XAxis type="number" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-3464" />
                        <YAxis dataKey="name" type="category" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.6)'
                  }} tickLine={false} axisLine={false} width={100} data-id="element-3465" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-3467" />} data-id="element-3466" />
                        <Bar dataKey="revenue" name="Ingresos" fill="#ec4899" radius={[0, 3, 3, 0]} barSize={20} opacity={0.8} data-id="element-3468" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>

            {/* ─── Tables Section ─────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3469">
              {/* Top Products Table */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden flex flex-col" data-id="element-3470">
                <div className="p-5 border-b border-white/[0.06]" data-id="element-3471">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3472">
                    Detalle de Productos
                  </h2>
                </div>
                <div className="overflow-x-auto flex-1" data-id="element-3473">
                  <table className="w-full text-sm" data-id="element-3474">
                    <thead data-id="element-3475">
                      <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-3476">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-3477">
                          Producto
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-3478">
                          Cant.
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-3479">
                          Ingresos
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-3480">
                      {data.topProducts.slice(0, 10).map((p, i) => <tr key={i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-3481">
                          <td className="px-5 py-3" data-id="element-3482">
                            <p className="font-medium text-white/90" data-id="element-3483">
                              {p.product_name}
                            </p>
                            {restaurantId === 'all' && <p className="text-xs text-white/40 mt-0.5" data-id="element-3484">
                                {p.restaurant_name}
                              </p>}
                          </td>
                          <td className="px-5 py-3 text-right text-white/60" data-id="element-3485">
                            {p.quantity}
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-emerald-400" data-id="element-3486">
                            {formatCurrency(p.revenue)}
                          </td>
                        </tr>)}
                      {data.topProducts.length === 0 && <tr data-id="element-3487">
                          <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-3488">
                            Sin datos
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recent Orders Table */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden flex flex-col" data-id="element-3489">
                <div className="p-5 border-b border-white/[0.06]" data-id="element-3490">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3491">
                    Órdenes Recientes
                  </h2>
                </div>
                <div className="overflow-x-auto flex-1" data-id="element-3492">
                  <table className="w-full text-sm" data-id="element-3493">
                    <thead data-id="element-3494">
                      <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-3495">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-3496">
                          ID / Cliente
                        </th>
                        <th className="text-left px-5 py-3 font-medium" data-id="element-3497">
                          Estado
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-3498">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-3499">
                      {data.recentOrders.slice(0, 10).map((o, i) => <tr key={o.id || i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-3500">
                          <td className="px-5 py-3" data-id="element-3501">
                            <p className="font-medium text-white/90" data-id="element-3502">
                              {String(o.id).substring(0, 8).toUpperCase()}
                            </p>
                            <p className="text-xs text-white/40 mt-0.5" data-id="element-3503">
                              {o.customer_name}
                            </p>
                          </td>
                          <td className="px-5 py-3" data-id="element-3504">
                            <span className="px-2 py-1 rounded-md text-[10px] font-medium uppercase tracking-wider bg-white/[0.06] text-white/70" data-id="element-3505">
                              {o.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-white" data-id="element-3506">
                            {formatCurrency(o.total)}
                          </td>
                        </tr>)}
                      {data.recentOrders.length === 0 && <tr data-id="element-3507">
                          <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-3508">
                            Sin datos
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>}
      </main>
    </div>;
}